import { useState, useEffect } from "react";
import { useAppSelector } from "src/hooks/useStoreHooks";
import { useLazyGetDialogByUserIdQuery } from "src/store/chat/api";
import { Dialog, DialogLastMessage, MessageType } from "../types";
import { CurrentUser } from "src/store/User/types";
import { useLazyGetUserQuery } from "src/store/User/api";
import avatar_skeleton from "src/assets/images/avatar_skeleton.png";

type UserDialogToOpen =
//uninitialized state
{
  dialog: null,
  user: null,
  isUninitialized: true,
  isLoading: false,
  isSuccess: false,
  isError: false,
} |
//loading state
{
  dialog: null,
  user: null,
  isUninitialized: false,
  isLoading: true,
  isSuccess: false,
  isError: false,
} |
//success state
{
  dialog: Dialog | null,
  user: Omit<CurrentUser, "is_using_password">,
  isUninitialized: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
} |
//error state
{
  dialog: null,
  user: null,
  isUninitialized: false,
  isLoading: false,
  isSuccess: false,
  isError: true,
};

const useUserDialogToOpen = () => {

  const [result, setResult] = useState<UserDialogToOpen>({
    dialog: null,
    user: null,
    isUninitialized: true,
    isLoading: false,
    isSuccess: false,
    isError: false,
  });

  const userIdToOpenDialogWith = useAppSelector(state => state.chat.userIdToOpenDialogInChatWith);
  const [getDialogByUserId, getDialogByUserIdState] = useLazyGetDialogByUserIdQuery();
  const [getUserByUserId, getUserByUserIdState] = useLazyGetUserQuery();

  useEffect(() => {
    if (!userIdToOpenDialogWith.userId)
      return;

    getDialogByUserId({ userId: userIdToOpenDialogWith.userId }, false);
    getUserByUserId({ userId: userIdToOpenDialogWith.userId }, false);

    setResult({
      dialog: null,
      user: null,
      isUninitialized: false,
      isLoading: true,
      isSuccess: false,
      isError: false,
    });

  }, [userIdToOpenDialogWith]);

  useEffect(() => {
    if (getDialogByUserIdState.isUninitialized || getUserByUserIdState.isUninitialized)
      return;

    if (getDialogByUserIdState.isFetching || getUserByUserIdState.isFetching) {
      setResult({
        dialog: null,
        user: null,
        isUninitialized: false,
        isLoading: true,
        isSuccess: false,
        isError: false,
      });
      return;
    }

    //if 404 then dialog with the user does not exist yet, it's ok
    //any other error is not ok
    if (getDialogByUserIdState.isError &&
        !("status" in getDialogByUserIdState.error && getDialogByUserIdState.error.status === 404)) {

      setResult({
        dialog: null,
        user: null,
        isUninitialized: false,
        isLoading: false,
        isSuccess: false,
        isError: true,
      });
      return;
    }

    if (!getUserByUserIdState.isSuccess) {
      setResult({
        dialog: null,
        user: null,
        isUninitialized: false,
        isLoading: false,
        isSuccess: false,
        isError: true,
      });
      return;
    }

    //preparation of information to result

    let dialog: Dialog | null = null;

    const dataDialog = getDialogByUserIdState.data;

    if (dataDialog) {

      const dataDialogLastMessage = dataDialog.last_message;

      let lastMessage: DialogLastMessage | null = null;

      if (dataDialogLastMessage)
        if (dataDialogLastMessage.type === MessageType.User)
          lastMessage = {
            type: MessageType.User,
            text: dataDialogLastMessage.text,
            username: dataDialogLastMessage.username,
          };
        else if (dataDialogLastMessage.type === MessageType.System)
          lastMessage = {
            type: MessageType.System,
            systemMessageType: dataDialogLastMessage.system_metadata.type,
          };

      dialog = {
        type: dataDialog.type,
        system: false,
        id: dataDialog.chat_id,
        title: dataDialog.title,
        avatarUrl: dataDialog.avatar_url || avatar_skeleton,
        isVerified: true,
        isRead: true,
        isOnline: true,
        isWarning: lastMessage?.type === MessageType.System,
        isMute: false,
        lastMessage,
        unreadMessagesCount: dataDialog.unread_messages_count,
      };
    }

    setResult({
      dialog,
      user: getUserByUserIdState.data.user,
      isUninitialized: false,
      isLoading: false,
      isSuccess: true,
      isError: false,
    });

  }, [getDialogByUserIdState, getUserByUserIdState]);

  return result;
};

export default useUserDialogToOpen;
